import { AppConcept, Feature } from '../types';

// Gemini API integration for generating app concepts
export const generateAppConcept = async (userIdea: string): Promise<AppConcept> => {
  try {
    // Call our secure backend API instead of Gemini directly
    const concept = await generateWithBackend(userIdea);
    return concept;
  } catch (error) {
    console.error('Error generating concept with backend');
    // Fallback to mock data if API fails
    return generateMockConcept(userIdea);
  }
};

const generateWithBackend = async (userIdea: string): Promise<AppConcept> => {
  // Call our secure backend API
  const response = await fetch('/api/generate-concept', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userIdea: userIdea
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Backend API error:', errorText);
    throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  
  if (!data.success || !data.concept) {
    throw new Error('Invalid response from backend API');
  }

  const conceptData = data.concept;

  // Create the final AppConcept object
  const concept: AppConcept = {
    id: generateId(),
    originalIdea: userIdea,
    appName: conceptData.appName,
    name: conceptData.appName,
    tagline: conceptData.tagline,
    emoji: conceptData.emoji,
    problem: conceptData.problem,
    targetAudience: Array.isArray(conceptData.targetAudience) ? conceptData.targetAudience : [conceptData.targetAudience],
    coreFeatures: conceptData.coreFeatures,
    mvpScope: conceptData.mvpScope,
    monetization: conceptData.monetization,
    techStack: conceptData.techStack,
    createdAt: new Date()
  };

  return concept;
};

const generateMockConcept = (userIdea: string): AppConcept => {
  const concepts = [
    {
      appName: "PlantChat",
      tagline: "Your plants' digital voice",
      emoji: "🌱",
      problem: "Plant owners struggle to understand their plants' needs, leading to overwatering, underwatering, and unhealthy plants. Many people want to care for plants but lack the knowledge to interpret visual cues and provide proper care.",
      targetAudience: [
        "Indoor plant enthusiasts (ages 25-40)",
        "New plant parents seeking guidance",
        "Busy professionals with houseplants",
        "People living in apartments with limited outdoor space"
      ],
      coreFeatures: [
        {
          title: "AI Plant Personality",
          description: "Each plant gets a unique personality that communicates through chat",
          icon: "🤖"
        },
        {
          title: "Photo Diagnosis",
          description: "Snap a photo to get instant plant health analysis",
          icon: "📸"
        },
        {
          title: "Daily Chat Updates",
          description: "Plants send daily messages about their mood and needs",
          icon: "💬"
        },
        {
          title: "Care Calendar",
          description: "Automated watering and fertilizing reminders",
          icon: "📅"
        }
      ],
      mvpScope: "Basic plant identification, care reminders, simple chat interface with plant personality, photo-based health diagnosis, and push notifications for care tasks.",
      monetization: "Freemium model: Basic plant care free, premium features include advanced plant personalities, detailed care guides, plant disease diagnosis, and unlimited plant profiles ($4.99/month).",
      techStack: ["React Native", "Firebase", "Plant.id API", "OpenAI GPT-4", "Push Notifications", "Camera API"]
    },
    {
      appName: "CoffeeSync",
      tagline: "Smart hydration meets coffee addiction",
      emoji: "☕",
      problem: "Coffee lovers often drink too much caffeine while neglecting proper hydration. This leads to jitters, crashes, and dehydration. People need a way to balance their coffee intake with water consumption for optimal health and energy.",
      targetAudience: [
        "Office workers who drink multiple cups daily",
        "Students during exam periods",
        "Remote workers with irregular schedules",
        "Health-conscious coffee enthusiasts"
      ],
      coreFeatures: [
        {
          title: "Smart Mug Integration",
          description: "Detects when you're drinking coffee vs water",
          icon: "☕"
        },
        {
          title: "Hydration Balance",
          description: "Tracks coffee-to-water ratio for optimal health",
          icon: "💧"
        },
        {
          title: "Energy Optimization",
          description: "Suggests best times for coffee vs water",
          icon: "⚡"
        },
        {
          title: "Social Challenges",
          description: "Compete with friends for healthy habits",
          icon: "🏆"
        }
      ],
      mvpScope: "Basic drink tracking app with manual input, hydration reminders, simple ratio tracking, and daily/weekly reports. Smart mug integration for v2.0.",
      monetization: "Hardware + software model: Smart mug ($49.99) + premium app features ($2.99/month) including detailed analytics, custom reminders, and social features.",
      techStack: ["React Native", "Bluetooth LE", "IoT Sensors", "Firebase", "HealthKit", "WearOS"]
    },
    {
      appName: "LocationBookmarks",
      tagline: "Bookmark the real world like web pages",
      emoji: "📍",
      problem: "People discover amazing places but forget exact locations, struggle to organize and share location-based memories, and have difficulty planning return visits to favorite spots.",
      targetAudience: [
        "Travel enthusiasts and explorers",
        "Food lovers seeking new restaurants",
        "Photographers documenting locations",
        "Local adventurers discovering their city"
      ],
      coreFeatures: [
        {
          title: "Smart Bookmarking",
          description: "One-tap location saving with automatic tagging",
          icon: "📍"
        },
        {
          title: "Memory Capture",
          description: "Add photos, notes, and ratings to each location",
          icon: "📸"
        },
        {
          title: "Collection Organization",
          description: "Create themed collections like 'Best Coffee Shops'",
          icon: "📂"
        },
        {
          title: "Social Sharing",
          description: "Share location collections with friends",
          icon: "👥"
        }
      ],
      mvpScope: "Basic location bookmarking, photo attachments, simple categorization, offline access, and sharing via standard platforms.",
      monetization: "Freemium: Basic bookmarking free, premium features include unlimited collections, advanced search, collaborative collections, and export options ($3.99/month).",
      techStack: ["React Native", "Google Maps API", "Firebase", "Core Location", "Cloud Storage", "Share Extensions"]
    }
  ];

  // Simple keyword matching to return relevant concept
  const lowerIdea = userIdea.toLowerCase();
  
  if (lowerIdea.includes('plant') || lowerIdea.includes('garden') || lowerIdea.includes('flower')) {
    return createConceptWithId(concepts[0], userIdea);
  } else if (lowerIdea.includes('coffee') || lowerIdea.includes('drink') || lowerIdea.includes('mug') || lowerIdea.includes('water')) {
    return createConceptWithId(concepts[1], userIdea);
  } else if (lowerIdea.includes('location') || lowerIdea.includes('place') || lowerIdea.includes('bookmark') || lowerIdea.includes('map')) {
    return createConceptWithId(concepts[2], userIdea);
  }

  // Default to a dynamically generated concept
  return createDynamicConcept(userIdea);
};

const createConceptWithId = (concept: any, originalIdea: string): AppConcept => {
  return {
    id: generateId(),
    originalIdea,
    createdAt: new Date(),
    ...concept
  };
};

const createDynamicConcept = (userIdea: string): AppConcept => {
  // Generate a concept based on the user's idea
  const appName = generateAppName(userIdea);
  const tagline = generateTagline(userIdea);
  
  return {
    id: generateId(),
    originalIdea: userIdea,
    appName,
    name: appName,
    tagline,
    emoji: generateEmoji(userIdea),
    problem: `Many people have challenges related to ${extractKeywords(userIdea).join(', ')}. This creates frustration and missed opportunities for better experiences.`,
    targetAudience: [
      "Tech-savvy users aged 18-35",
      "People interested in innovative solutions",
      "Early adopters of mobile apps",
      "Users looking for convenience and efficiency"
    ],
    coreFeatures: generateFeatures(userIdea),
    mvpScope: `A simple mobile app that addresses the core problem with basic functionality, user-friendly interface, and essential features for ${extractKeywords(userIdea)[0] || 'the main use case'}.`,
    monetization: "Freemium model with basic features free and premium features available through subscription ($2.99/month) or one-time purchase ($9.99).",
    techStack: ["React Native", "Firebase", "Push Notifications", "Cloud Storage", "REST API"],
    createdAt: new Date()
  };
};

const generateAppName = (idea: string): string => {
  const keywords = extractKeywords(idea);
  const prefixes = ['Smart', 'Quick', 'Easy', 'My', 'Pocket', 'Daily', 'Instant'];
  const suffixes = ['Hub', 'Pro', 'Assist', 'Helper', 'Buddy', 'Sync', 'Connect'];
  
  if (keywords.length > 0) {
    const mainKeyword = capitalize(keywords[0]);
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    
    return Math.random() > 0.5 ? `${prefix}${mainKeyword}` : `${mainKeyword}${suffix}`;
  }
  
  return "IdeaApp";
};

const generateTagline = (idea: string): string => {
  const keywords = extractKeywords(idea);
  const templates = [
    `Making ${keywords[0] || 'life'} simple and smart`,
    `Your personal ${keywords[0] || 'productivity'} companion`,
    `Revolutionizing how you handle ${keywords[0] || 'daily tasks'}`,
    `Smart solutions for ${keywords[0] || 'modern problems'}`
  ];
  
  return templates[Math.floor(Math.random() * templates.length)];
};

const generateFeatures = (idea: string): Feature[] => {
  const keywords = extractKeywords(idea);
  const baseFeatures = [
    {
      title: "Smart Detection",
      description: `Automatically detects and analyzes ${keywords[0] || 'relevant information'}`,
      icon: "🔍"
    },
    {
      title: "Personalized Insights",
      description: "Learns from your usage patterns to provide better recommendations",
      icon: "📊"
    },
    {
      title: "Quick Actions",
      description: "One-tap solutions for common tasks and workflows",
      icon: "⚡"
    },
    {
      title: "Social Integration",
      description: "Share and collaborate with friends and family",
      icon: "👥"
    }
  ];
  
  return baseFeatures;
};

const extractKeywords = (text: string): string[] => {
  const stopWords = ['what', 'if', 'why', 'how', 'can', 'could', 'would', 'should', 'the', 'a', 'an', 'and', 'or', 'but', 'my', 'your', 'i', 'you', 'we', 'they'];
  const words = text.toLowerCase().split(/\s+/).filter(word => 
    word.length > 2 && !stopWords.includes(word) && /^[a-zA-Z]+$/.test(word)
  );
  return words.slice(0, 3);
};

const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const generateEmoji = (idea: string): string => {
  const keywords = extractKeywords(idea);
  const emojiMap: { [key: string]: string } = {
    'phone': '📱',
    'app': '📱',
    'food': '🍕',
    'music': '🎵',
    'game': '🎮',
    'book': '📚',
    'photo': '📸',
    'camera': '📸',
    'car': '🚗',
    'bike': '🚲',
    'plant': '🌱',
    'water': '💧',
    'coffee': '☕',
    'alarm': '⏰',
    'clock': '⏰',
    'mirror': '🪞',
    'shoe': '👟',
    'shoes': '👟',
    'weather': '🌤️',
    'fitness': '💪',
    'health': '🏥',
    'money': '💰',
    'shopping': '🛒',
    'travel': '✈️',
    'home': '🏠',
    'work': '💼',
    'social': '👥',
    'chat': '💬',
    'message': '💬',
    'email': '📧',
    'calendar': '📅',
    'task': '✅',
    'note': '📝',
    'location': '📍',
    'map': '🗺️',
    'security': '🔒',
    'password': '🔐'
  };

  // Find matching emoji based on keywords
  for (const keyword of keywords) {
    if (emojiMap[keyword]) {
      return emojiMap[keyword];
    }
  }

  // Default emojis if no match found
  const defaultEmojis = ['💡', '🚀', '⭐', '🔥', '✨', '🎯', '🌟', '💫'];
  return defaultEmojis[Math.floor(Math.random() * defaultEmojis.length)];
};

const generateId = (): string => {
  // Generate a more robust unique ID
  return `concept_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// For production, replace with actual OpenAI API integration:
/*
const generateWithOpenAI = async (userIdea: string): Promise<AppConcept> => {
  const stages = [
    {
      prompt: `Transform this random idea: "${userIdea}" into a viable mobile app concept. Focus on the core problem it could solve and primary use case. Return a JSON object with appName, tagline, and problem fields.`,
      type: 'concept'
    },
    {
      prompt: `For this app concept: [PREVIOUS_OUTPUT], generate 6-8 specific features that would make this useful and engaging for users. Return as a features array.`,
      type: 'features'
    },
    {
      prompt: `Analyze this app idea [CONCEPT] and suggest target audience (array of 4-5 specific user groups), monetization strategies, and MVP scope.`,
      type: 'business'
    },
    {
      prompt: `For this app [CONCEPT], recommend appropriate tech stack as an array of 5-7 technologies for a small development team.`,
      type: 'technical'
    }
  ];

  const results = {};
  for (const stage of stages) {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: 'You are an expert product manager and startup advisor. Generate realistic, actionable app concepts.'
          },
          {
            role: 'user',
            content: stage.prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    const data = await response.json();
    results[stage.type] = data.choices[0].message.content;
  }

  // Parse and combine results into AppConcept object
  return parseOpenAIResponse(results, userIdea);
};
*/